import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { Unit, Lesson } from '@mira-avant/mtlibs';
import { UnitCreatePayload, UnitUpdatePayload } from '@mira-avant/mtlibs';
import { LanguageService } from './language.service';
import { LessonService } from './lesson.service';
import { ApiService } from './api.service';

@Injectable({
	providedIn: 'root',
})
export class UnitService {
	private readonly BASE_PATH = 'units';

	public loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public units$: BehaviorSubject<Unit[]> = new BehaviorSubject<Unit[]>([]);

	constructor(
		private api: ApiService,
		private languageService: LanguageService,
		private lessonService: LessonService
	) {}

	public async onCourseChange$(courseUuid: string | undefined): Promise<void> {
		this.units$.next([]);

		if (!courseUuid) {
			return;
		}

		await this.getByCourse$(courseUuid);
	}

	public setActiveText(unit: Unit): void {
		const currentLang: string = this.languageService.spokenLanguage$.value;
		const activeText = unit.unit_text.find(unitText => unitText.default_lang_id === currentLang);
		unit.active_text = activeText || unit.unit_text[0];
	}

	public sortUnits(units: Unit[]): Unit[] {
		return units.sort((a: Unit, b: Unit) => {
			if (a.display_order !== b.display_order) {
				return a.display_order - b.display_order;
			}

			if (!a.active_text || !b.active_text) {
				return 0;
			}

			return a.active_text.name.localeCompare(b.active_text.name);
		});
	}

	public processUnit(unit: Unit): Unit {
		this.setActiveText(unit);
		if (unit.lesson && Array.isArray(unit.lesson)) {
			unit.lesson = this.lessonService.processLessons(unit.lesson);
		}
		return unit;
	}

	public processUnits(units: Unit[]): Unit[] {
		return this.sortUnits(units.map(unit => this.processUnit(unit)));
	}

	private async withLoading<T>(operation: () => Promise<T>): Promise<T> {
		this.loading$.next(true);
		try {
			return await operation();
		} finally {
			this.loading$.next(false);
		}
	}

	public async get$(uuid: string, expandChildren: boolean = false, expandAll: boolean = false): Promise<Unit> {
		return this.withLoading(async () => {
			const params = {
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const unit = await firstValueFrom(this.api.get<Unit>(`${this.BASE_PATH}/get-by-uuid/${uuid}`, { params }));
			return this.processUnit(unit);
		});
	}

	public async create$(unitData: UnitCreatePayload): Promise<Unit> {
		return this.withLoading(async () => {
			const newUnit = await firstValueFrom(this.api.post<Unit>(this.BASE_PATH, unitData));

			const processedUnit = this.processUnit(newUnit);
			const currentUnits = this.units$.value;
			const updatedUnits = this.processUnits([...currentUnits, processedUnit]);
			this.units$.next(updatedUnits);
			return processedUnit;
		});
	}

	public async update$(uuid: string, unitData: UnitUpdatePayload): Promise<Unit> {
		return this.withLoading(async () => {
			const updatedUnit = await firstValueFrom(this.api.put<Unit>(`${this.BASE_PATH}/${uuid}`, unitData));

			const processedUnit = this.processUnit(updatedUnit);
			const currentUnits = this.units$.value;
			const updatedUnits = this.processUnits(
				currentUnits.map(unit => (unit.uuid === uuid ? processedUnit : unit))
			);
			this.units$.next(updatedUnits);
			return processedUnit;
		});
	}

	public async delete$(uuid: string): Promise<void> {
		return this.withLoading(async () => {
			await firstValueFrom(this.api.delete<void>(`${this.BASE_PATH}/${uuid}`));

			const currentUnits = this.units$.value;
			const updatedUnits = currentUnits.filter(unit => unit.uuid !== uuid);
			this.units$.next(updatedUnits);
		});
	}

	public async getByCourse$(
		courseUuid: string,
		expandChildren: boolean = false,
		expandAll: boolean = false
	): Promise<Unit[]> {
		return this.withLoading(async () => {
			const params = {
				courseUuid,
				expandChildren: expandChildren.toString(),
				expandAll: expandAll.toString(),
			};

			const units = await firstValueFrom(this.api.get<Unit[]>(`${this.BASE_PATH}/course`, { params }));

			const processedUnits = this.processUnits(units);
			this.units$.next(processedUnits);
			return processedUnits;
		});
	}

	public async duplicate$(uuid: string, courseParentUuid: string): Promise<Unit> {
		return this.withLoading(async () => {
			const duplicatedUnit = await firstValueFrom(
				this.api.post<Unit>(`${this.BASE_PATH}/duplicate/${uuid}/${courseParentUuid}`, {})
			);

			const processedUnit = this.processUnit(duplicatedUnit);
			const currentUnits = this.units$.value;
			const updatedUnits = this.processUnits([...currentUnits, processedUnit]);
			this.units$.next(updatedUnits);
			return processedUnit;
		});
	}

	public async duplicateBulk$(uuids: string[], courseParentUuid: string): Promise<Unit[]> {
		return this.withLoading(async () => {
			const duplicatedUnits = await firstValueFrom(
				this.api.post<Unit[]>(`${this.BASE_PATH}/duplicate-bulk/${courseParentUuid}`, { uuids })
			);
			const processedUnits = this.processUnits(duplicatedUnits);
			const currentUnits = this.units$.value;
			const newUuidSet = new Set(processedUnits.map(unit => unit.uuid));
			const filteredCurrent = currentUnits.filter(unit => !newUuidSet.has(unit.uuid));
			const updatedUnits = this.processUnits([...processedUnits, ...filteredCurrent]);
			this.units$.next(updatedUnits);
			return processedUnits;
		});
	}

	public async moveUp$(uuid: string, courseUuid: string): Promise<Unit[]> {
		return this.withLoading(async () => {
			const updatedUnits = await firstValueFrom(
				this.api.post<Unit[]>(`${this.BASE_PATH}/move-up/${uuid}/${courseUuid}`, {})
			);

			const processedUnits = this.processUnits(updatedUnits);
			this.units$.next(processedUnits);
			return processedUnits;
		});
	}

	public async moveDown$(uuid: string, courseUuid: string): Promise<Unit[]> {
		return this.withLoading(async () => {
			const updatedUnits = await firstValueFrom(
				this.api.post<Unit[]>(`${this.BASE_PATH}/move-down/${uuid}/${courseUuid}`, {})
			);

			const processedUnits = this.processUnits(updatedUnits);
			this.units$.next(processedUnits);
			return processedUnits;
		});
	}
}
